import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { movementBadgeClass, movementLabel } from "../lib/display";
import { useAuth } from "../lib/auth";
import useAutoRefresh from "../lib/useAutoRefresh";
import {
  Search,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRightLeft,
  Trash2,
  X,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

function MovementIcon({ type }) {
  if (type === "giris") return <ArrowDownToLine size={10} strokeWidth={3} />;
  if (type === "cikis") return <ArrowUpFromLine size={10} strokeWidth={3} />;
  if (type === "transfer") return <ArrowRightLeft size={10} strokeWidth={3} />;
  return null;
}

export default function Movements() {
  const { user } = useAuth();
  const isAdmin = ["admin", "supervisor"].includes(user?.role);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState("selected"); // 'selected' | 'all-filtered'
  const [deleting, setDeleting] = useState(false);

  const reload = () => {
    setLoading(true);
    api
      .get("/movements", { params: { type, q, limit: 500 } })
      .then(({ data }) => {
        setData(data);
        setSelected(new Set());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/movements", { params: { type, q, limit: 500 } })
        .then(({ data }) => {
          if (!cancelled) {
            setData(data);
            setSelected(new Set());
          }
        })
        .catch(() => {})
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [type, q]);

  useAutoRefresh(reload);

  const allSelected = useMemo(
    () => data.items.length > 0 && data.items.every((m) => selected.has(m.id)),
    [data.items, selected],
  );

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(data.items.map((m) => m.id)));
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const body =
        confirmMode === "all-filtered"
          ? { ids: [], all_matching: true, type }
          : { ids: Array.from(selected) };
      const { data: res } = await api.post("/movements/bulk-delete", body);
      toast.success(`${res.deleted} hareket silindi`);
      setConfirmOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const askConfirm = (mode) => {
    setConfirmMode(mode);
    setConfirmOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-3)]"
          />
          <input
            data-testid="movements-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Barkod, ürün, müşteri, proje, kullanıcı…"
            className="w-full h-11 pl-9 pr-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none"
          />
        </div>
        <div className="flex hard border-2 border-[color:var(--border)] overflow-hidden">
          {[
            { value: "", label: "Tümü" },
            { value: "giris", label: "Giriş" },
            { value: "cikis", label: "Çıkış" },
            { value: "transfer", label: "Transfer" },
          ].map((t) => (
            <button
              key={t.value}
              onClick={() => setType(t.value)}
              data-testid={`movements-filter-${t.value || "all"}`}
              className={`px-4 h-[42px] text-sm font-semibold uppercase tracking-wider ${
                type === t.value
                  ? "bg-[color:var(--primary)] text-white"
                  : "bg-white hover:bg-[color:var(--bg)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {isAdmin && (
          <button
            data-testid="movements-delete-all-filtered"
            onClick={() => askConfirm("all-filtered")}
            disabled={data.total === 0}
            className="h-[42px] px-4 hard border-2 border-[color:var(--error)] text-[color:var(--error)] hover:bg-[color:var(--error)] hover:text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-40 transition-colors"
            title="Şu anki filtreye uyan tüm hareketleri sil"
          >
            <Trash2 size={14} strokeWidth={2.5} /> Filtreyi Sil
          </button>
        )}
        <div className="ticker text-[color:var(--text-3)] ml-auto">
          {loading ? "Yükleniyor…" : `${data.total.toLocaleString("tr-TR")} hareket`}
        </div>
      </div>

      {selected.size > 0 && (
        <div
          data-testid="movements-bulk-actionbar"
          className="hard border-2 border-[color:var(--primary)] bg-[color:var(--primary)] text-white p-3 flex items-center justify-between row-enter"
        >
          <div className="flex items-center gap-3">
            <span className="ticker">/// SEÇİLİ</span>
            <span className="font-mono font-bold text-lg">{selected.size}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 hard bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors"
            >
              Seçimi Temizle
            </button>
            {isAdmin ? (
              <button
                data-testid="movements-bulk-delete-btn"
                onClick={() => askConfirm("selected")}
                className="px-4 py-1.5 hard bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors"
              >
                <Trash2 size={14} strokeWidth={2.5} /> Toplu Sil
              </button>
            ) : (
              <span className="text-xs opacity-80">Silme yetkiniz yok (Yönetici)</span>
            )}
          </div>
        </div>
      )}

      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)]">
        <div className="overflow-auto" style={{ maxHeight: "75vh" }}>
          <table className="dense-table w-full">
            <thead>
              <tr>
                {isAdmin && (
                  <th style={{ width: 36 }}>
                    <input
                      data-testid="movements-select-all"
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="w-4 h-4 accent-[color:var(--primary)]"
                    />
                  </th>
                )}
                <th>Tip</th>
                <th>Tarih</th>
                <th>Barkod</th>
                <th>Ürün</th>
                <th>Adet</th>
                <th>Sehpa</th>
                <th>Müşteri</th>
                <th>Proje</th>
                <th>Kullanıcı</th>
                <th>Fiş</th>
                <th>Not</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && !loading && (
                <tr>
                  <td colSpan={isAdmin ? 12 : 11} className="py-16 text-center text-[color:var(--text-3)]">
                    Hareket bulunamadı.
                  </td>
                </tr>
              )}
              {data.items.map((m) => {
                const checked = selected.has(m.id);
                return (
                  <tr
                    key={m.id}
                    data-testid="movement-row"
                    className={checked ? "bg-[color:var(--accent-bg)]" : ""}
                  >
                    {isAdmin && (
                      <td>
                        <input
                          data-testid="movement-row-checkbox"
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(m.id)}
                          className="w-4 h-4 accent-[color:var(--primary)]"
                        />
                      </td>
                    )}
                    <td>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase hard ${movementBadgeClass(m.type)}`}
                      >
                        <MovementIcon type={m.type} />
                        {movementLabel(m.type)}
                      </span>
                    </td>
                    <td className="text-[color:var(--text-2)] whitespace-nowrap">
                      {m.created_at ? new Date(m.created_at).toLocaleString("tr-TR") : ""}
                    </td>
                    <td className="font-mono">{m.barkod}</td>
                    <td className="max-w-xs truncate">{m.isim}</td>
                    <td className="font-mono font-bold">{m.adet}</td>
                    <td className="font-mono">{m.sehpa_konumu || "-"}</td>
                    <td>{m.musteri || "-"}</td>
                    <td>{m.proje || "-"}</td>
                    <td>{m.user_name || m.user_email}</td>
                    <td className="font-mono text-xs">{m.fis_id || "-"}</td>
                    <td className="max-w-xs truncate text-xs text-[color:var(--text-2)]">
                      {m.note}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white hard border-2 border-[color:var(--border)]">
            <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
              <div>
                <div className="ticker text-[color:var(--error)]">
                  /// YÖNETİCİ ONAYI
                </div>
                <div className="font-bold text-lg mt-1">Hareket Silme</div>
              </div>
              <button
                onClick={() => setConfirmOpen(false)}
                className="p-2 hard hover:bg-[color:var(--bg)]"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {confirmMode === "all-filtered" ? (
                <>
                  <p className="text-sm">
                    Şu anki filtreye ({type ? movementLabel(type) : "TÜMÜ"}) uyan{" "}
                    <b className="text-[color:var(--error)]">{data.total}</b> adet
                    hareket <b>kalıcı olarak silinecek</b>. Envanterdeki stok
                    verileri etkilenmez.
                  </p>
                </>
              ) : (
                <p className="text-sm">
                  Seçili{" "}
                  <b className="text-[color:var(--error)]">{selected.size}</b>{" "}
                  hareket kaydı kalıcı olarak silinecek. Bu işlem geri alınamaz.
                </p>
              )}
              <div className="hard border border-[color:var(--warning)] bg-[color:var(--warning-bg)] text-[color:var(--warning)] p-3 text-xs flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>Hareket kayıtları silindikten sonra geri getirilemez.</span>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setConfirmOpen(false)}
                  disabled={deleting}
                  className="flex-1 h-11 hard border-2 border-[color:var(--border)] hover:bg-[color:var(--bg)] font-semibold disabled:opacity-50"
                >
                  Vazgeç
                </button>
                <button
                  data-testid="movements-delete-confirm"
                  onClick={doDelete}
                  disabled={deleting}
                  className="flex-1 h-11 hard bg-[color:var(--error)] hover:opacity-90 text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
                >
                  <Trash2 size={16} strokeWidth={2.5} />
                  {deleting ? "Siliniyor…" : "Evet, Sil"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
